/**
 * CREATIVE DETONATOR - Global Footer Injection
 *
 * Injects consistent footer HTML across all public pages.
 * Dashboard pages use their own layout and do NOT have footers.
 * Auth pages use minimal layout and should NOT use this module.
 */

import { getPathPrefix, isDashboardPage, isAuthPage } from './layout-utils.js';

/**
 * Generate footer HTML with correct paths
 * @param {string} prefix - Path prefix for assets
 * @returns {string} Footer HTML
 */
function getFooterHTML(prefix) {
  const currentYear = new Date().getFullYear();

  return `
<footer class="footer">
  <div class="container">
    <div class="footer-content">
      <div class="footer-brand">
        <span class="footer-logo">CREATIVE DETONATOR</span>
        <p class="footer-tagline">A System for Explosive Advertising Ideas</p>
      </div>
      <nav class="footer-nav">
        <div class="footer-nav-group">
          <h4 class="footer-nav-title">EXPLORE</h4>
          <a href="${prefix}features/method/pages/index.html" class="footer-link">The Method</a>
          <a href="${prefix}features/triggers/pages/index.html" class="footer-link">Trigger Library</a>
        </div>
        <div class="footer-nav-group">
          <h4 class="footer-nav-title">METHOD</h4>
          <a href="${prefix}features/method/pages/chamber.html" class="footer-link">Detonation Chamber</a>
          <a href="${prefix}features/method/pages/sequence.html" class="footer-link">Detonation Sequence</a>
          <a href="${prefix}features/method/pages/test.html" class="footer-link">Explosion Test</a>
        </div>
        <div class="footer-nav-group">
          <h4 class="footer-nav-title">RESOURCES</h4>
          <a href="${prefix}features/grid-splitter/index.html" class="footer-link">Storyboard Grid Splitter</a>
          <a href="${prefix}about-method/index.html" class="footer-link">About</a>
        </div>
      </nav>
    </div>
    <div class="footer-bottom">
      <p class="footer-copyright">Creative Detonator &copy; ${currentYear}</p>
      <!-- Phase 1: Subtle login link for creatives (hidden when logged in) -->
      <a href="${prefix}features/auth/pages/login.html" class="footer-login" id="footer-login-link">Login</a>
    </div>
  </div>
</footer>
`;
}

/**
 * Injects global footer into page
 * @param {string} containerId - ID of placeholder element (default: 'global-footer')
 */
export function injectFooter(containerId = 'global-footer') {
  // Skip injection for dashboard and auth pages
  if (isDashboardPage() || isAuthPage()) {
    return;
  }

  const container = document.getElementById(containerId);
  if (!container) {
    // Footer is optional - some pages (like generator) may not have one
    return;
  }

  const prefix = getPathPrefix();
  container.innerHTML = getFooterHTML(prefix);

  // Dispatch event for consistency with header
  window.dispatchEvent(new CustomEvent('layout:footer-injected'));
  console.log('[Layout] Footer injected');
}

/**
 * Auto-inject footer on DOM ready if placeholder exists
 */
function autoInject() {
  const container = document.getElementById('global-footer');
  if (container) {
    injectFooter('global-footer');
  }
}

// Auto-inject on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInject);
} else {
  autoInject();
}
